import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';

const gpuPlans = [
  { name: 'Nvidia Tesla T4', eur: 1.15, profile: 'Entry PC', specs: ['4 vCPU', '16 GB RAM', '100 GB SSD'] },
  { name: 'Nvidia L4', eur: 3.36, profile: 'Balanced GPU', specs: ['8 vCPU', '32 GB RAM', '100 GB SSD'] },
  { name: 'Nvidia A10G', eur: 4.34, profile: 'Creative GPU', specs: ['8 vCPU', '32 GB RAM', '100 GB SSD'] },
];

const vpsPlans = [
  { name: 'VPS Start', eur: 0.03, profile: 'Utility', specs: ['2 vCPU', '2 GB RAM', '50 GB SSD'] },
  { name: 'VPS Team', eur: 0.09, profile: 'API and staging', specs: ['4 vCPU', '8 GB RAM', '50 GB SSD'] },
  { name: 'VPS Core', eur: 0.31, profile: 'Production backend', specs: ['16 vCPU', '32 GB RAM', '50 GB SSD'] },
];

const platformCards = [
  {
    title: 'Cloud GPU',
    text: 'For AI training, inference, rendering, and compute-heavy product tasks.',
    note: 'Production-ready profiles',
  },
  {
    title: 'Cloud VPS',
    text: 'For backend services, APIs, dashboards, and private internal tools.',
    note: 'Linux and Windows options',
  },
];

const workflow = [
  {
    title: 'Choose profile',
    text: 'Pick GPU or VPS based on your workload.',
  },
  {
    title: 'Set configuration',
    text: 'Select compute options and schedule start time.',
  },
  {
    title: 'Launch and track',
    text: 'Deploy and monitor status from one dashboard.',
  },
];

function Home() {
  const { formatFromEur } = useCurrency();
  const [activeTab, setActiveTab] = useState('gpu');
  const [activeSlide, setActiveSlide] = useState(0);
  const [activePlatformSlide, setActivePlatformSlide] = useState(0);
  const [activeWorkflowSlide, setActiveWorkflowSlide] = useState(0);
  const priceGridRef = useRef(null);
  const platformGridRef = useRef(null);
  const workflowGridRef = useRef(null);
  const plans = useMemo(() => (activeTab === 'gpu' ? gpuPlans : vpsPlans), [activeTab]);

  useEffect(() => {
    setPageMeta({
      title: 'Clouniq | Cloud GPU & VPS Platform',
      description: 'Launch cloud GPU and VPS resources with transparent pricing, fast setup, and simple operations.',
    });
  }, []);

  useEffect(() => {
    setActiveSlide(0);
    if (priceGridRef.current) {
      priceGridRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [activeTab]);

  const getSlideMetrics = () => {
    const container = priceGridRef.current;
    if (!container) return null;
    const firstCard = container.querySelector('.home-board-price-card');
    if (!firstCard) return null;

    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard.getBoundingClientRect().width + gap;
    return { container, cardWidth };
  };

  const getTrackMetrics = (trackRef, cardSelector) => {
    const container = trackRef.current;
    if (!container) return null;
    const firstCard = container.querySelector(cardSelector);
    if (!firstCard) return null;

    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard.getBoundingClientRect().width + gap;
    return { container, cardWidth };
  };

  const handlePricingScroll = (event) => {
    const container = event.currentTarget;
    const firstCard = container.querySelector('.home-board-price-card');
    if (!firstCard) return;
    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard.getBoundingClientRect().width + gap;
    if (!cardWidth) return;
    const nextIndex = Math.round(container.scrollLeft / cardWidth);
    const boundedIndex = Math.max(0, Math.min(plans.length - 1, nextIndex));
    if (boundedIndex !== activeSlide) {
      setActiveSlide(boundedIndex);
    }
  };

  const scrollToSlide = (targetIndex) => {
    const metrics = getSlideMetrics();
    if (!metrics) return;
    const boundedIndex = Math.max(0, Math.min(plans.length - 1, targetIndex));
    metrics.container.scrollTo({
      left: boundedIndex * metrics.cardWidth,
      behavior: 'smooth',
    });
    setActiveSlide(boundedIndex);
  };

  const syncSlideFromScroll = (event, itemCount, cardSelector, setSlideState) => {
    const container = event.currentTarget;
    const firstCard = container.querySelector(cardSelector);
    if (!firstCard) return;
    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard.getBoundingClientRect().width + gap;
    if (!cardWidth) return;
    const nextIndex = Math.round(container.scrollLeft / cardWidth);
    const boundedIndex = Math.max(0, Math.min(itemCount - 1, nextIndex));
    setSlideState((prev) => (prev === boundedIndex ? prev : boundedIndex));
  };

  const scrollTrackToSlide = (trackRef, targetIndex, itemCount, cardSelector, setSlideState) => {
    const metrics = getTrackMetrics(trackRef, cardSelector);
    if (!metrics) return;
    const boundedIndex = Math.max(0, Math.min(itemCount - 1, targetIndex));
    metrics.container.scrollTo({
      left: boundedIndex * metrics.cardWidth,
      behavior: 'smooth',
    });
    setSlideState(boundedIndex);
  };

  return (
    <div className="home-board">
      <section className="home-board-hero">
        <div className="container">
          <div className="home-board-hero-grid">
            <div className="home-board-hero-main">
              <span className="section-tag">CLOUNIQ Platform</span>
              <h1>Modern cloud compute for teams that ship weekly</h1>
              <p>
                Deploy GPU and VPS workloads with clean pricing, quick launch, and one dashboard for operations.
              </p>
              <div className="home-board-actions">
                <Link to="/register" className="btn btn-primary">Start setup</Link>
                <a href="#pricing" className="btn btn-outline-primary">View pricing</a>
              </div>
            </div>

            <aside className="home-board-hero-side">
              <div className="home-board-visual-v2">
                <img src="/assets/illustrations/hero-orbit.svg" alt="Cloud control board interface" />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section id="solutions" className="home-board-platform">
        <div className="container">
          <div className="home-board-head">
            <span className="section-tag">Platform</span>
            <h2>Simple structure for core workflows</h2>
          </div>
          <div className="home-board-slider-controls" aria-label="Platform slider controls">
            <button
              type="button"
              className="home-board-slider-arrow"
              onClick={() => scrollTrackToSlide(platformGridRef, activePlatformSlide - 1, platformCards.length, '.home-board-card', setActivePlatformSlide)}
              disabled={activePlatformSlide <= 0}
              aria-label="Previous platform card"
            >
              &#8592;
            </button>
            <button
              type="button"
              className="home-board-slider-arrow"
              onClick={() => scrollTrackToSlide(platformGridRef, activePlatformSlide + 1, platformCards.length, '.home-board-card', setActivePlatformSlide)}
              disabled={activePlatformSlide >= platformCards.length - 1}
              aria-label="Next platform card"
            >
              &#8594;
            </button>
          </div>
          <div
            className="home-board-platform-grid"
            ref={platformGridRef}
            onScroll={(event) => syncSlideFromScroll(event, platformCards.length, '.home-board-card', setActivePlatformSlide)}
          >
            {platformCards.map((card) => (
              <article className="home-board-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
                <span>{card.note}</span>
              </article>
            ))}
          </div>
          <div className="home-board-slider-hint" aria-hidden="true">
            {platformCards.map((card, index) => (
              <span key={card.title} className={`home-board-slider-dot ${index === activePlatformSlide ? 'active' : ''}`}></span>
            ))}
          </div>
        </div>
      </section>

      <section className="home-board-workflow">
        <div className="container">
          <div className="home-board-head">
            <span className="section-tag">Workflow</span>
            <h2>From request to running environment</h2>
          </div>
          <div className="home-board-slider-controls" aria-label="Workflow slider controls">
            <button
              type="button"
              className="home-board-slider-arrow"
              onClick={() => scrollTrackToSlide(workflowGridRef, activeWorkflowSlide - 1, workflow.length, '.home-board-step', setActiveWorkflowSlide)}
              disabled={activeWorkflowSlide <= 0}
              aria-label="Previous workflow card"
            >
              &#8592;
            </button>
            <button
              type="button"
              className="home-board-slider-arrow"
              onClick={() => scrollTrackToSlide(workflowGridRef, activeWorkflowSlide + 1, workflow.length, '.home-board-step', setActiveWorkflowSlide)}
              disabled={activeWorkflowSlide >= workflow.length - 1}
              aria-label="Next workflow card"
            >
              &#8594;
            </button>
          </div>
          <div
            className="home-board-workflow-grid"
            ref={workflowGridRef}
            onScroll={(event) => syncSlideFromScroll(event, workflow.length, '.home-board-step', setActiveWorkflowSlide)}
          >
            {workflow.map((item, index) => (
              <article key={item.title} className="home-board-step">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <div className="home-board-slider-hint" aria-hidden="true">
            {workflow.map((item, index) => (
              <span key={item.title} className={`home-board-slider-dot ${index === activeWorkflowSlide ? 'active' : ''}`}></span>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="home-board-pricing">
        <div className="container">
          <div className="home-board-head">
            <span className="section-tag">Pricing</span>
            <h2>Choose profile and launch</h2>
          </div>

          <div className="xp-plan-toggle home-board-toggle">
            <button
              type="button"
              className={activeTab === 'gpu' ? 'active' : ''}
              onClick={() => setActiveTab('gpu')}
            >
              Cloud PC
            </button>
            <button
              type="button"
              className={activeTab === 'vps' ? 'active' : ''}
              onClick={() => setActiveTab('vps')}
            >
              Cloud VPS
            </button>
          </div>

          <div className="home-board-slider-controls" aria-label="Pricing slider controls">
            <button
              type="button"
              className="home-board-slider-arrow"
              onClick={() => scrollToSlide(activeSlide - 1)}
              disabled={activeSlide <= 0}
              aria-label="Previous plan"
            >
              &#8592;
            </button>
            <button
              type="button"
              className="home-board-slider-arrow"
              onClick={() => scrollToSlide(activeSlide + 1)}
              disabled={activeSlide >= plans.length - 1}
              aria-label="Next plan"
            >
              &#8594;
            </button>
          </div>

          <div className="home-board-price-grid" ref={priceGridRef} onScroll={handlePricingScroll}>
            {plans.map((plan, index) => (
              <article key={plan.name} className={`home-board-price-card ${index === 1 ? 'featured' : ''}`}>
                <div className="home-board-price-top">
                  <span>{plan.profile}</span>
                  {index === 1 && <b>Popular</b>}
                </div>
                <h3>{plan.name}</h3>
                <p className="home-board-price">{formatFromEur(plan.eur)} / hour</p>
                <ul>
                  {plan.specs.map((spec) => <li key={spec}>{spec}</li>)}
                </ul>
                <Link to="/register" className="btn btn-primary">Deploy</Link>
              </article>
            ))}
          </div>

          <div className="home-board-slider-hint" aria-hidden="true">
            {plans.map((plan, index) => (
              <span
                key={plan.name}
                className={`home-board-slider-dot ${index === activeSlide ? 'active' : ''}`}
              ></span>
            ))}
          </div>
        </div>
      </section>

      <section className="home-board-cta">
        <div className="container">
          <div className="home-board-cta-shell">
            <div>
              <h2>Need help selecting the right setup?</h2>
              <p>Tell us your workload and we will suggest the best GPU or VPS profile.</p>
            </div>
            <div className="home-board-actions">
              <Link to="/contact" className="btn btn-outline-primary">Talk to team</Link>
              <Link to="/register" className="btn btn-primary">Create account</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;



